// Patches the global console the moment this module is imported.
// Must be the first import in index.tsx so it runs before any framework code.
// Also captures resource-load failures and unhandled promise rejections
// which never go through console.* and cannot be caught any other way.

const MAX_ENTRIES = 200;
const _logs: string[] = [];
const _listeners: Array<() => void> = [];

const stamp = () =>
  new Date().toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const push = (level: string, args: unknown[]) => {
  const msg = args
    .map((a) => {
      if (a instanceof Error) return a.message;
      if (typeof a === "object" && a !== null) {
        try { return JSON.stringify(a); } catch { return String(a); }
      }
      return String(a);
    })
    .join(" ");
  _logs.unshift(`[${stamp()}] ${level}: ${msg}`);
  if (_logs.length > MAX_ENTRIES) _logs.length = MAX_ENTRIES;
  _listeners.forEach((fn) => fn());
};

const origLog = console.log.bind(console);
const origWarn = console.warn.bind(console);
const origError = console.error.bind(console);
const origDebug = console.debug.bind(console);

console.log = (...args: unknown[]) => { origLog(...args); push("LOG", args); };
//console.warn  = (...args: unknown[]) => { origWarn(...args);  push("WARN", args); };
console.error = (...args: unknown[]) => { origError(...args); push("ERR", args); };
console.debug = (...args: unknown[]) => { origDebug(...args); push("DBG", args); };

// Resource load failures (video, img, script, link) — browser-generated,
// never reach console.* so this is the only way to capture them.
window.addEventListener(
  "error",
  (ev) => {
    const t = ev.target as (EventTarget & { src?: string; href?: string }) | null;
    if (t && ("src" in t || "href" in t)) {
      push("RES", [`Failed to load: ${t.src ?? t.href ?? "unknown"}`]);
    }
  },
  true, // capture phase — fires before the element's own error handler
);

// Unhandled promise rejections
window.addEventListener("unhandledrejection", (ev) => {
  push("ERR", [`Unhandled rejection: ${ev.reason}`]);
});

export const debugCapture = {
  getLogs: (): readonly string[] => _logs,
  subscribe: (fn: () => void): (() => void) => {
    _listeners.push(fn);
    return () => {
      const i = _listeners.indexOf(fn);
      if (i !== -1) _listeners.splice(i, 1);
    };
  },
};
